import { define } from 'wicked-elements'
import { sectionsSource, sectionsIcons } from './ParkSections'
import onIntersect from 'on-intersect'
import { ResizeObserver } from '@juggle/resize-observer'
import { colors, toUri } from '../../helpers.js'
import bus from '../../bus.js'

const isHover = window.matchMedia('(hover: hover)')

mapboxgl.accessToken = 'pk.eyJ1IjoidGhlcmFpbHBhcmsiLCJhIjoiY2t5eDZ1NW93MGZkbTJ2cGkwem5oOGpoOSJ9.YPXYAcnmwNScodtpfx8ccQ'

define('[data-component~="map"]', {
  init () {
    // map init
    this.map = new mapboxgl.Map({
      container: this.element,
      style: 'mapbox://styles/therailpark/ckyxhc160000x14qvg5l2i6c7',
      center: [-75.169253, 39.965453],
      zoom: this.getInitialZoom(),
      minZoom: 9,
      logoPosition: 'bottom-right',
      attributionControl: false
    })

    // state
    this.state = {
      mapVisible: false,
      mapData: null,
      touchPopup: false,
      hoverPopup: false,
      permanentPopup: false,
      touchActive: true,
      hoverActive: true,
      permanentActive: true,
      hoverId: null
    }

    // popups
    this.touchPopup = null
    this.hoverPopup = null
    this.permanentPopup = null

    // bound methods
    this.handleEnter = this.handleEnter.bind(this)
    this.handleExit = this.handleExit.bind(this)
    this.handleResize = this.handleResize.bind(this)
    this.handleSetLocation = this.handleSetLocation.bind(this)
    this.handleClosePermanentPopup = this.handleClosePermanentPopup.bind(this)
    this.handleResetMap = this.handleResetMap.bind(this)
  },
  connected () {
    this.map.on('load', () => {
      this.addSource()
      this.loadIcons()
      this.addControls()
      this.addSections()
      this.addClusters()
      this.addClustersCount()
      this.addUnclusteredPoints()
      this.addPopups()
      this.handleClusterClicks()
      this.setInitialLocation()
      
      if (isHover.matches) {
        this.handlePointHovers()
        this.handleClusterHovers()
        this.handlePointClicks()
      } else {
        this.handlePointClicks()
        // this.handlePointTaps()
      }

      // Disable tap drag zoom: https://docs.mapbox.com/mapbox-gl-js/api/handlers/#touchzoomrotatehandler
      this.map.touchZoomRotate._tapDragZoom.disable()
    })

    this.stopObserving = onIntersect(this.element, {}, this.handleEnter, this.handleExit)
    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this.element)
    bus.on('map:set-location', this.handleSetLocation)
    bus.on('map:close-permanent-popup', this.handleClosePermanentPopup)
    bus.on('map:reset', this.handleResetMap)
  },
  disconnected () {
    this.stopObserving()
    this.resizeObserver.disconnect()
    this.map.remove()
    this.map = null
    bus.removeListener('map:set-location', this.handleSetLocation)
    bus.removeListener('map:close-permanent-popup', this.handleClosePermanentPopup)
    bus.removeListener('map:reset', this.handleResetMap)
  },
  getInitialZoom () {
    return window.innerWidth > 1024
      ? 14.25
      : window.innerWidth > 650
      ? 13.5
      : 13.25
  },
  addSource () {
    this.map.addSource('locations', {
      type: 'geojson',
      data: `${window.SITE_BASE}/data/locations`,
      cluster: true,
      clusterMaxZoom: 14, // Max zoom to cluster points on
      clusterRadius: 50 // Radius of each cluster when clustering points (defaults to 50)
    })
  },
  loadIcons () {
    sectionsIcons.forEach(icon => {
      let customIcon = new Image(384, 100);
      customIcon.onload = () => this.map.addImage(icon.name, customIcon)
      customIcon.src = icon.src
    })
  },
  addControls () {
    this.map.addControl(new mapboxgl.NavigationControl({
      showCompass: false
    }), 'bottom-left')
    this.map.addControl(new mapboxgl.AttributionControl({
      compact: true
    }), 'bottom-right')
  },
  addSections () {
    this.map.addSource('sectionsSource', sectionsSource)
    this.map.addLayer({
      'id': 'sectionLabels',
      'type': 'symbol',
      'source': 'sectionsSource',
      'minzoom': 13,
      'layout': {
        'icon-image': ['get', 'icon'],
        'icon-anchor': ['get', 'anchor'],
        'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.15, 14.5, 0.5],
        'icon-allow-overlap': true,
        'text-allow-overlap': false
      }
    })
  },
  addClusters () {
    this.map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'locations',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#fff',
        'circle-radius': 23,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#000'
      }
    })
  },
  addClustersCount () {
    this.map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'locations',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['Armin Grotesk Normal'],
        'text-size': 23,
        'text-offset': [0,0.05]
      }
    })
  },
  addUnclusteredPoints () {
    this.map.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'locations',
      filter: [
        '!has', 'point_count'
      ],
      paint: {
        'circle-color': [
          'coalesce',
          ['get', 'color'], // Set color based on property.color value
          '#fff'
        ],
        'circle-radius': 13,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#000'
      }
    })
  },
  addPopups () {
    this.touchPopup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      maxWidth: this.popupWidth(),
      offset: [ 0, -22 ]
    })

    this.hoverPopup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      maxWidth: this.popupWidth(),
      offset: [ 0, -22 ]
    })

    this.permanentPopup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      maxWidth: this.popupWidth(),
      offset: [ 0, -22 ]
    })
  },
  popupWidth () {
    return window.innerWidth < 640 ? '270px' : '300px'
  },
  renderPopups (e) {
    const {
      touchPopup,
      hoverPopup,
      permanentPopup,
      touchActive,
      hoverActive,
      permanentActive,
    } = this.state

    if (touchActive && touchPopup) {
      if (this.touchPopup) this.openPopup(touchPopup, e, this.touchPopup, true)  
    } else {
      if (this.touchPopup) this.touchPopup.remove()
    }

    if (hoverActive && hoverPopup) {
      if (this.hoverPopup) this.openPopup(hoverPopup, e, this.hoverPopup)  
    } else {
      if (this.hoverPopup) this.hoverPopup.remove()
    }

    if (permanentActive && permanentPopup) {
      if (this.permanentPopup) this.openPopup(permanentPopup, e, this.permanentPopup)
    } else {
      if (this.permanentPopup) this.permanentPopup.remove()
    }
  },
  duplicatePopupCheck (featureA, featureB) {
    try {
      return featureA.properties.uri === featureB.properties.uri
    } catch (e) {
      return false
    }
  },
  openPopup (location, event, popup, touch = false) {
    const coordinates = location.geometry.coordinates.slice()
    const title = location.properties.title
    const excerpt = location.properties.excerpt
    const category = location.properties.category
    const date = location.properties.date
    const url = location.properties.url
     
    if (event) {
      while (Math.abs(event.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += event.lngLat.lng > coordinates[0] ? 360 : -360;
      }
    }

    // const arrowLink = touch ? '<span class="link-arrow">→</span>' : ''
    const arrowLink = ''
    
    popup
      .setLngLat(coordinates)
      .setHTML(`<a href="${url}">
        <div class="space-y-1.5">
          <div class="text-sm">
            ${category ? `<div>${category}</div>` : ''}
            ${date ? `<div>${date}</div>` : ''}
          </div>
          <div class="text-sm md:text-base">${excerpt}</div>
          ${arrowLink}
        </div>
      </a>`)
      .addTo(this.map)
  },
  handleClusterClicks () {
    this.map.on('click', 'clusters',  (e) => {
      
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['clusters']
      })

      const clusterId = features[0].properties.cluster_id

      this.map.getSource('locations').getClusterExpansionZoom(
        clusterId,
        (err, zoom) => {
          if (err) return

          this.map.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom + 2 // deeper zoom to handle dense points
          })
        }
      )

    })
  },
  handlePointTaps () {
    this.map.on('click', 'unclustered-point', (e) => {
      e.preventDefault()
      
      if (this.duplicatePopupCheck(e.features[0], this.state.permanentPopup)) {
        this.state.touchPopup = false
        this.state.permanentActive = true
      } else {
        this.state.touchPopup = e.features[0]
        this.state.permanentActive = false
      }

      this.renderPopups(e)
    })

    // on tap-outside
    this.map.on('click', (e) => {
      if (e.defaultPrevented === false) {
        this.state.touchPopup = false
        this.state.permanentActive = true
        this.renderPopups()
      }
    })
  },
  /**
   * Use mousemove instead of mouseenter for more responsive hovers
   * https://docs.mapbox.com/mapbox-gl-js/example/hover-styles/
   */
  handlePointHovers () {
    this.map.on('mousemove', 'unclustered-point', (e) => {
      if (e.features.length > 0) {
        if (e.features[0].properties.uri !== this.state.hoverId) {
          this.state.hoverId = e.features[0].properties.uri

          this.map.getCanvas().style.cursor = 'pointer'

          if (this.duplicatePopupCheck(e.features[0], this.state.permanentPopup)) {
            this.state.hoverPopup = false
            this.state.permanentActive = true
          } else {
            this.state.hoverPopup = e.features[0]
            this.state.permanentActive = false
          }
          
          this.renderPopups(e)
        }
      }
    })

    this.map.on('mouseleave', 'unclustered-point', (e) => {
      this.map.getCanvas().style.cursor = ''
      this.state.hoverId = null
      this.state.hoverPopup = false
      this.state.permanentActive = true
      this.renderPopups(e)
    })
  },
  handleClusterHovers () {
    this.map.on('mouseenter', 'clusters', () => {
      this.map.getCanvas().style.cursor = 'pointer'
    })
    
    this.map.on('mouseleave', 'clusters', () => {
      this.map.getCanvas().style.cursor = ''
    })
  },
  handlePointClicks () {
    this.map.on('click', 'unclustered-point', (e) => {
      const { url, uri } = e.features[0].properties
      const currentUri = toUri(window.RPTC_APP.state.location)
      if (currentUri !== uri) {
        window.RPTC_HISTORY.push(window.location.pathname)
        window.RPTC_APP.go(url)  
      }
    })
  },
  /**
   * Set initial location if starting on an Entry page
   */
  async setInitialLocation () {
    const currentUri = toUri(window.RPTC_APP.state.location)
    const EntryViewRegex = new RegExp('^index\/.+')
    if (EntryViewRegex.test(currentUri)) {
      this.handleSetLocation(currentUri)
    }
  },
  /**
   * Resize map when it re-enters viewport.
   * Handles mobile map size bug
   */
  handleEnter () {
    this.state.mapVisible = true
    if (this.map) { this.map.resize() }
  },
  handleExit () {
    this.state.mapVisible = false
  },
  /**
   * Flys to location based on uri
   */
  async handleSetLocation (uri) {
    // Exit if map is hidden (mobile views)
    if (!this.state.mapVisible) return

    // 1. Fetch data if necessary
    if (!this.state.mapData) {
      const response = await fetch(`${window.SITE_BASE}/data/locations`)
      this.state.mapData = await response.json()
    }

    // 2. Lookup location by uri, and fly to if possible
    try {
      const location = this.state.mapData.features.find(element => element.id === uri)
      if (location) {
        // Set fly to options
        let options = {
          center: location.geometry.coordinates
        }

        // -> only zoom in when zoomed out
        if (this.map.getZoom() < 15) {
          options.zoom = 17
        }

        // -> disable curve when zoomed in
        if (this.map.getZoom() >= 15 && this.map.getBounds().contains(location.geometry.coordinates)) {
          options.curve = 0
        }

        // -> set padding when on desktop
        if (window.innerWidth >= 768) {
          options.padding = { right: window.innerWidth / 2 }
        } else {
          // options.padding = { top: window.innerHeight / 5 }
          options.padding = { top: 0, right: 0 }
        }

        // Fly to location
        this.map.flyTo(options)

        // Open permanent popup
        this.state.permanentPopup = location
        this.state.touchPopup = false
        if (isHover.matches) {
          this.state.permanentActive = true  
        } else {
          this.state.permanentActive = false
        }
        this.renderPopups()
      } else {
        this.handleResetMap(true)
      }
    } catch (e) { }
  },
  /**
   * Closes permanent popup
   */
  handleClosePermanentPopup () {
    this.state.permanentPopup = false
    this.renderPopups()
  },
  handleResize (entries, observer) {
    this.map.resize()
  },
  handleResetMap (withPadding = false) {
    let padding = { right: 0, top: 0 }
    if (withPadding) {
      padding = window.innerWidth >= 768
        ? { right: window.innerWidth / 2, top: 0 }
        : { top: 0, right: 0 }
        // : { top: window.innerHeight / 5, right: 0 }
    }

    this.map.flyTo({
      center: [-75.169253, 39.965453],
      zoom: this.getInitialZoom(),
      bearing: 0,
      pitch: 0,
      padding: padding
    })
  }
})